import { AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppExpander from "@toothfairy/shared-ui/AppExpander";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import ToothFairyButton from "../../../Components/ToothFairyComponents/ToothFairyButton";
import Constants from "../../../Constants";
import RechargeSheet from "../../../Components/Credits/RechargeSheet";
import AutoRechargeControl from "../../../Components/Credits/AutoRechargeControl";
import SpendCapControl from "../../../Components/Credits/SpendCapControl";
import useCredits from "../../../Hooks/useCredits";
import { API_Credits } from "../../../API/API_Consumption/credits";
import { DEFAULT_AUTO_RECHARGE, formatUoI, formatUsd } from "../../../Constants/billing";
import { isLargeScreen } from "../../MainViewContainer";

// BillingCredits: "Credits & limits" section for the billing settings screen.
// Shows the user-level credit balance + recharge, the ACCOUNT-level monthly
// maximum spend (hard stop across all workspaces, on Stripe customer metadata)
// and auto-recharge. Nothing here is workspace-scoped.
//
// Source of truth for spendCap / autoRecharge is the Stripe customer object
// (`stripeCustomerData`) — the same one fetched at auth and refreshed by
// `onCreditsSaved`. `useCredits` is used for the balance + the save call.
const BillingCredits = ({ stripeCustomerData, onCreditsSaved, rechargeOpen, setRechargeOpen, credits }) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const creditsInstance = credits || useCredits();
  // Recharge sheet open state can be controlled by the parent (so the progress
  // bar's "Add credits to start" can trigger it) or owned locally as a fallback.
  const [localRechargeOpen, setLocalRechargeOpen] = useState(false);
  const isRechargeOpen = rechargeOpen ?? localRechargeOpen;
  const handleSetRechargeOpen = setRechargeOpen || setLocalRechargeOpen;

  // Drafts reported by the child controls. `undefined` = not yet touched (so we
  // fall back to the saved value); a reported value — including `null` for
  // "disabled / no cap" — means the user explicitly changed it. Using `null` as
  // the initial sentinel would make "untouched" indistinguishable from the user
  // switching the spend cap OFF, which wrongly keeps the Save button disabled.
  const [spendCapDraft, setSpendCapDraft] = useState(undefined);
  const [autoRechargeDraft, setAutoRechargeDraft] = useState(undefined);

  // Authoritative saved values, parsed from the Stripe customer metadata.
  const savedSpendCap = API_Credits.parseSpendCap(stripeCustomerData).monthlyMaxUsd ?? null;
  const savedAutoRecharge =
    API_Credits.parseRechargeConfig(stripeCustomerData).autoRecharge ??
    DEFAULT_AUTO_RECHARGE;
  // Auto-recharge charges the customer's default payment method off-session.
  // If there's no saved card, auto-recharge can never fire — surface that.
  const hasPaymentMethod =
    !!stripeCustomerData?.invoice_settings?.default_payment_method;

  const spendCapDirty =
    spendCapDraft !== undefined && spendCapDraft !== savedSpendCap;
  const autoRechargeDirty =
    autoRechargeDraft !== undefined &&
    JSON.stringify(autoRechargeDraft) !== JSON.stringify(savedAutoRecharge);
  const isDirty = spendCapDirty || autoRechargeDirty;

  const handleSave = async () => {
    // eslint-disable-next-line no-console
    console.log("[billingCredits] handleSave START", {
      spendCapDraft,
      autoRechargeDraft,
      savedSpendCap,
      savedAutoRecharge,
      spendCapDirty,
      autoRechargeDirty,
    });
    try {
      const capToSave = spendCapDirty
        ? spendCapDraft // may be null (cap off) — persist as-is
        : savedSpendCap;
      const arToSave = autoRechargeDirty
        ? autoRechargeDraft
        : savedAutoRecharge;
      // eslint-disable-next-line no-console
      console.log("[billingCredits] handleSave persisting (single POST)", {
        capToSave,
        arToSave,
      });
      // Single atomic POST for both — avoids the read-merge-write race that
      // clobbers metadata when the two are sent concurrently.
      await creditsInstance.updateBillingSettings({
        spendCap: capToSave,
        autoRecharge: arToSave,
      });
      // eslint-disable-next-line no-console
      console.log("[billingCredits] handleSave persisted, refreshing");
      // Re-sync from the backend so the displayed value reflects what was
      // actually persisted (avoids any optimistic/stale revert).
      await creditsInstance.refresh({ silent: true });
      // eslint-disable-next-line no-console
      console.log("[billingCredits] handleSave refreshed, credits now", {
        spendCap: creditsInstance.spendCap,
        autoRecharge: creditsInstance.autoRecharge,
      });
      // Also refresh the cached stripeCustomerData (App.js / billing details
      // view hold a separate copy that is otherwise stale after a save).
      onCreditsSaved?.();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log("[billingCredits] handleSave FAILED", e);
    }
  };

  return (
    <AppExpander
      title={t("credits.billingSectionTitle")}
      icon={AppAssets.icons.cost}
      style={{ minWidth: isLargeScreen() ? 650 : 250 }}
      isExpandedOnLoad
    >
      <View style={{ padding: 8 }}>
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: currentTheme?.theme?.softBackgroundColor,
            borderRadius: 10,
            padding: 12,
            marginBottom: 12,
          }}
        >
          <View>
            <AppText
              style={{
                fontSize: 12,
                color: currentTheme?.theme?.secondary_font_color,
              }}
            >
              {t("credits.recharge.currentBalance")}
            </AppText>
            <AppText style={{ fontSize: 22, fontWeight: "bold" }}>
              {formatUsd(creditsInstance.balanceUsd)}
            </AppText>
            <AppText
              style={{
                fontSize: 12,
                color: currentTheme?.theme?.primary,
                fontWeight: "bold",
              }}
            >
              {formatUoI(creditsInstance.balanceUoI)} {t("credits.indicator.uoi")}
            </AppText>
          </View>
          <ToothFairyButton
            onPress={() => handleSetRechargeOpen(true)}
            title={t("credits.indicator.recharge")}
            paddingLeft={10}
            paddingRight={10}
            wrapperStyle={{ alignSelf: "center" }}
          />
        </View>

        <SpendCapControl
          value={savedSpendCap}
          onDraftChange={setSpendCapDraft}
        />

        <AppText style={{ fontWeight: "bold", marginTop: 16 }}></AppText>
        <AutoRechargeControl
          value={savedAutoRecharge}
          onDraftChange={setAutoRechargeDraft}
          hasPaymentMethod={hasPaymentMethod}
        />

        <ToothFairyButton
          onPress={handleSave}
          isDisabled={!isDirty || creditsInstance.saving}
          awesomeIconColor={currentTheme?.theme?.white}
          backgroundColor={currentTheme?.theme?.primary}
          btnContentStyle={{ paddingLeft: 0, paddingRight: 0 }}
          style={{
            height: Constants.appButtonHeight,
            width: "auto",
            shadowColor: currentTheme?.theme?.transparent,
            marginHorizontal: 10,
          }}
          wrapperStyle={{
            width: "fit-content",
            minWidth: 150,
            marginTop: 20,
            height: Constants.appButtonHeight,
            alignSelf: "center",
          }}
          title={creditsInstance.saving ? t("common.saving") : t("common.save")}
        />

        <RechargeSheet
          isModalVisible={isRechargeOpen}
          closeModal={() => handleSetRechargeOpen(false)}
          credits={creditsInstance}
          returnTo="/profile/billing"
        />
      </View>
    </AppExpander>
  );
};

export default BillingCredits;
